
import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { mapGetters } from "vuex";
import api from "../../api";
import * as accountTypes from "@account/store/types";
import { LoadingBar } from "@appbase/components/loading";
import TemplateDesignHeader from "./TemplateDesignHeader.vue";
import Designer from "./Designer.vue";
import { Part, partDefault, Parts } from "../common/parts/types";

export type TemplateDesignPageInfo = {
    pages: webpartv3.WebpartPage[] | null;
    /** 메뉴관리에서 정의한 웹파트 페이지 아이디 */
    currentPageId: number | null;
    templates: webpartv3.WebpartTemplate[] | null;
    /** 페이지 템플릿 아이디 */
    currentTemplateId: string | null;
};

@Component({
    components: { TemplateDesignHeader, LoadingBar, Designer },

    computed: mapGetters({
        current: accountTypes.CURRENT,
    }),
})
export default class TemplateDesignPersonal extends Vue {
    @Prop({ type: String, required: true }) company!: string;
    @Prop({ type: Boolean, default: false }) personal!: boolean;

    current!: account.TCurrent;

    template: webpartv3.WebpartTemplate = Object.create(null);
    webparts: Parts = [];
    options: TemplateDesignPageInfo = Object.create(null);
    loading: boolean = false;

    async created() {
        await this.load();
    }

    async load(cache: boolean = true) {
        this.loading = true;
        const options: TemplateDesignPageInfo = {
            pages: null,
            currentPageId: null,
            templates: null,
            currentTemplateId: null,
        };

        // 자신을 맴버로 가지고 있는 권한 그룹이 할당된 웹파트 목록과
        // 자신에게 할당된 웹파트 템플릿 조회
        const [template, webparts] = await Promise.all([
            this.portalCache.webpartsv3.getMyTemplate(cache),
            this.portalCache.webpartsv3.getPrivilegedWebparts(),
        ]);
        const { webparts: ids } = this.current.permissions;

        console.log("templates", template);

        this.template = { ...template, isDefault: false } as webpartv3.WebpartTemplate;

        // 웹파트 페이지에 해당하는 템플릿을 가져온다.
        const { id: templateId, cols, rows, rowHeight, designWidth, parts: designParts } = template;

        // 디자이너 레이아웃에 배치되지 않은 웹파트 필터링
        const partIds = designParts?.map((x) => x.id);
        const adds = webparts.filter((x) => !partIds?.includes(x.id));
        // 웹파트 레이아웃에서 사용 가능한 모든 웹파트
        if (designParts) {
            this.webparts = [
                ...designParts.map<Part>((x) => {
                    // 디자인파트에 포함된 웹파트와 아이디가 일치하는 웹파트
                    var t = webparts.filter((i) => i.id == x.id);
                    var prop = { preview: true };
                    if (t && t.length > 0 && t[0].props) {
                        // 저장된 옵션 있는경우 JSON 파싱
                        prop = { ...JSON.parse(t[0].props as string), preview: true };
                    }
                    return {
                        ...partDefault,
                        ...x,
                        webpartId: x.id,
                        available: !x.templateId,
                        lock: x.lock || false,
                        props: prop,
                    };
                }),
                ...adds.map<Part>((x) => {
                    // 저장된 옵션 있는 경우 JSON 파싱
                    var prop = x.props ? { ...JSON.parse(x.props as string), preview: true } : { preview: true };
                    return {
                        ...partDefault,
                        ...(x as any),
                        webpartId: x.id,
                        templateId: templateId,
                        available: true,
                        lock: false,
                        props: prop,
                        type: "part",
                        fixedColcnt: x.columns,
                        fixedRowcnt: x.rows,
                    };
                }),
            ];
        } else {
            this.webparts = webparts.map<Part>((x) => {
                // 저장된 옵션 있는 경우 JSON 파싱
                var prop = x.props ? { ...JSON.parse(x.props as string), preview: true } : { preview: true };
                return {
                    ...partDefault,
                    ...(x as any),
                    webpartId: x.id,
                    templateId: templateId,
                    available: true,
                    lock: false,
                    props: prop,
                    type: "part",
                    fixedColcnt: x.columns,
                    fixedRowcnt: x.rows,
                };
            });
        }
        this.webparts = this.webparts.filter((x) => ids.includes(x.webpartId));
        this.options = options;
        this.loading = false;
    }

    /**
     * 템플릿 디자인 데이터를 저장합니다.
     */
    async handleSave() {
        if (!this.template) return;
        const { id } = this.template;
        this.loading = true;
        const templateId = this.template.id!;
        const parts = this.webparts.filter((x) => !x.available).map<Part>((x) => ({ ...x, templateId: templateId, props: undefined }));
        const template: webpartv3.WebpartTemplate = {
            ...this.template,
            parts: parts,
        };

        await api.personal.publish(template);
        await (window as any).alertAsync(this.$t("M_Hours_HasBeenSaved").toString(), function () {
            window.location.reload();
        });
    }

    /**
     * 개인화 설정을 제거하고 템플릿 기본 설정을 가져옵니다.
     */
    async handleClear() {
        if (await (window as any).confirmAsync(this.$t("초기화하시겠습니까").toString())) {
            this.loading = true;
            await api.personal.delete();
            await (window as any).alertAsync(this.$t("M_Settings_Initialized").toString(), function () {
                window.location.reload();
            });
        }
    }
}
